<template>
  <v-app-bar id="default-app-bar" app absolute class="v-bar--underline" color="transparent" :clipped-left="$vuetify.rtl"
    :clipped-right="!$vuetify.rtl" height="70" flat
    :extended="showBottomBar && $vuetify.breakpoint.smAndDown"
    :extension-height="showBottomBar && $vuetify.breakpoint.smAndDown ? 56 : undefined">
    <v-app-bar-nav-icon class="hidden-md-and-up" @click="drawer = !drawer" />

    <default-drawer-toggle class="hidden-sm-and-down" />

    <v-toolbar-title
      class="font-weight-light text-h5 company-title"
      style="cursor: pointer"
      v-text="selectedCompanyName"
      @contextmenu.prevent="onCompanyTitleMenu"
      @touchstart.passive="startCompanyPress"
      @touchmove.passive="cancelCompanyPress"
      @touchend="cancelCompanyPress"
      @touchcancel="cancelCompanyPress"
    />

    <v-bottom-navigation
      v-if="showBottomBar && $vuetify.breakpoint.mdAndUp"
      v-model="navbar"
      color="primary"
      class="app-nav-bar app-nav-bar--inline"
    >
      <v-btn
        v-for="item in bottomBarItems"
        :key="item.path"
        v-feature-key="item.featureKey ? { key: item.featureKey, label: item.text } : null"
        :value="item.path"
      >
        <span>{{ item.text }}</span>
        <v-icon>{{ item.icon }}</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <v-spacer v-else />

    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn class="ml-3 mr-4" elevation="1" fab small v-on="on">
          <v-icon :color="currentGlobe">
            mdi-web
          </v-icon>
        </v-btn>
      </template>
      <span>{{
        currentGlobeId == 1
          ? "Mobile server working"
          : "Mobile server not working"
      }}</span>
    </v-tooltip>

    <default-account />

    <template v-if="showBottomBar && $vuetify.breakpoint.smAndDown" v-slot:extension>
      <v-bottom-navigation
        v-model="navbar"
        color="primary"
        class="app-nav-bar"
      >
        <v-btn
          v-for="item in bottomBarItems"
          :key="item.path"
          v-feature-key="item.featureKey ? { key: item.featureKey, label: item.text } : null"
          :value="item.path"
        >
          <span>{{ item.text }}</span>
          <v-icon>{{ item.icon }}</v-icon>
        </v-btn>
      </v-bottom-navigation>
    </template>

  </v-app-bar>
</template>

<script>
// Utilities
import { get, sync } from 'vuex-pathify'

export default {
  name: 'DefaultBar',
  components: {
    DefaultAccount: () =>
      import(
        /* webpackChunkName: "default-account" */
        './widgets/Account'
      ),
    DefaultDrawerToggle: () =>
      import(
        /* webpackChunkName: "default-drawer-toggle" */
        './widgets/DrawerToggle'
      ),
    DefaultGoHome: () =>
      import(
        /* webpackChunkName: "default-go-home" */
        './widgets/GoHome'
      ),
    // DefaultNotifications: () =>
    // import(
    //   /* webpackChunkName: "default-notifications" */
    //   './widgets/Notifications'
    // ),
    // DefaultSearch: () =>
    // import(
    //   /* webpackChunkName: "default-search" */
    //   './widgets/Search'
    // ),
  },
  data: () => ({
    companyPressTimer: null,
    currentGlobe: '#f82328',
    greenGlobe: '#45e689',
    redGlobe: '#f82328',
    currentGlobeId: 0,
  }),
  computed: {
    ...sync('app', ['drawer', 'mini']),
    ...get('company', ['selectedCompanyId', 'selectedCompanyName']),
    isSuperUser () {
      return this.$store.getters['authUser/isSuperUser']
    },
    bottomBarItems () {
      const all = [
        { text: 'Devices', icon: 'mdi-cellphone', path: 'devices', featureKey: 'navigation.devices' },
        { text: 'Device Groups', icon: 'mdi-group', path: 'device-groups', featureKey: 'navigation.deviceGroups' },
        { text: 'Apps', icon: 'mdi-apps', path: 'apps', featureKey: 'navigation.apps' },
        { text: 'Scripts', icon: 'mdi-script-text-outline', path: 'scripts' },
        { text: 'Configs', icon: 'mdi-tune', path: 'configs' },
        { text: 'QR', icon: 'mdi-qrcode-scan', path: 'kiosk-qr' },
        { text: 'QR List', icon: 'mdi-qrcode-edit', path: 'qr-provisioning' },
        { text: 'ESP32 OTA', icon: 'mdi-chip', path: 'esp32-ota', featureKey: 'navigation.esp32Ota', superUserOnly: true },
        { text: 'Settings', icon: 'mdi-cog-outline', path: 'settings', superUserOnly: true },
        { text: 'Licenses', icon: 'mdi-license', path: 'license', superUserOnly: true },
        { text: 'Users', icon: 'mdi-account-group', path: 'users' },
      ]
      const visible = all.filter(item => !item.superUserOnly || this.isSuperUser)
      if (this.isSuperUser) {
        visible.push({ text: 'Features', icon: 'mdi-flag-variant', path: 'feature-management' })
      }
      return visible
    },
    name: get('route/name'),
    path: get('route/path'),
    showBottomBar() {
      return this.selectedCompanyId > 0 && this.isPathInBottomBar()
    },
    navbar: {
      get() { return this.currentNavPath() },
      set(value) {
        if (!this.showBottomBar) {
          return;
        }

        if (value === 'kiosk-qr' && !this.isSuperUser) {
          this.$signalHub.$emit('open-company-provisioning-qr')
          return
        }

        // Absolute path: a relative push would resolve under /<slug>/.
        if (this.currentNavPath() !== value) {
          this.$router.push('/' + value)
        }
      },
    },
  },
  watch: {
    selectedCompanyId() {
      this.checkNavbarState()
    },
  },
  created() {
    // Listen to score changes coming from Signal events
    this.$signalHub.$on(
      'signal-status-changed',
      this.onSignalConnectionChanged,
    )

    this.checkNavbarState()
  },
  beforeDestroy() {
    this.$signalHub.$off(
      'signal-status-changed',
      this.onSignalConnectionChanged,
    )
  },
  methods: {
    onCompanyTitleMenu(e) {
      if (!this.selectedCompanyId) {
        return
      }
      this.$signalHub.$emit('open-company-menu', { x: e.clientX, y: e.clientY })
    },
    startCompanyPress(e) {
      if (!this.selectedCompanyId) {
        return
      }
      const touch = e.touches ? e.touches[0] : e
      clearTimeout(this.companyPressTimer)
      this.companyPressTimer = setTimeout(() => {
        this.$signalHub.$emit('open-company-menu', { x: touch.clientX, y: touch.clientY })
      }, 500)
    },
    cancelCompanyPress() {
      clearTimeout(this.companyPressTimer)
    },
    currentNavPath() {
      // Devices may be served as /<slug>/devices.
      return this.name === 'Devices' ? 'devices' : this.path?.replace('/', '')
    },
    isPathInBottomBar() {
      const item = this.currentNavPath()
      return this.bottomBarItems.some(x => x.path === item)
    },
    checkNavbarState() {
      // if (this.selectedCompanyId === 0 && this.path !== '/devices') {
      //   this.$router.replace('/devices')
      // }
    },
    onSignalConnectionChanged(isConnected) {
      if (isConnected) {
        this.currentGlobe = this.greenGlobe
        this.currentGlobeId = 1
      } else {
        this.currentGlobe = this.redGlobe
        this.currentGlobeId = 0
      }
    },
  },
}
</script>
